import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { AuthContext } from "../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft,
  Layers,
  Trash2,
  Eye,
  Calendar,
  FileText,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Download,
  FileSpreadsheet,
  ChevronRight,
  ChevronDown,
  Combine,
  X,
  Search,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Bar,
  Line,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const tok = () => localStorage.getItem("token");
const fmt = (n) => `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/* ── List view (reused for both YTD and Grand) ─────────────────────── */
function MergedListView({ kind = "ytd" }) {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [selected, setSelected] = useState({}); // YTD-mode multiselect
  const [showSaveGrand, setShowSaveGrand] = useState(false);
  const [grandName, setGrandName] = useState("");
  const [savingGrand, setSavingGrand] = useState(false);

  const isYtd = kind === "ytd";
  const listEndpoint = isYtd
    ? `${API}/tax-helper/merged-statements`
    : `${API}/tax-helper/grand-statements`;
  const responseKey = isYtd ? "merged_statements" : "grand_statements";
  const headerTitle = isYtd ? "Year-to-Date Statements" : "Grand Statements";
  const headerSub = isYtd
    ? "Merged statements you've saved for combined views"
    : "Multi-year combined views built from your Year-to-Date statements";
  const HeaderIcon = isYtd ? Layers : Combine;

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(listEndpoint, {
        headers: { Authorization: `Bearer ${tok()}` },
      });
      setItems(res.data?.[responseKey] || []);
    } catch (err) {
      toast.error(`Failed to load ${isYtd ? "merged" : "grand"} statements`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.id) load();
  }, [user?.id, kind]); // eslint-disable-line

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/tax-helper/merged-statements/${id}`, {
        headers: { Authorization: `Bearer ${tok()}` },
      });
      setItems(prev => prev.filter(m => m.id !== id));
      setSelected(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      toast.success("Deleted");
    } catch (err) {
      toast.error("Failed to delete");
    } finally {
      setConfirmDeleteId(null);
    }
  };

  const selectedIds = Object.keys(selected).filter(id => selected[id]);
  const toggleSelect = (id) =>
    setSelected(prev => ({ ...prev, [id]: !prev[id] }));

  const openGrandDialog = () => {
    if (selectedIds.length < 2) {
      toast.error("Select at least 2 Year-to-Date statements to merge");
      return;
    }
    setGrandName(`Grand Statement · ${new Date().getFullYear()}`);
    setShowSaveGrand(true);
  };

  const saveGrand = async () => {
    if (!grandName.trim()) {
      toast.error("Name is required");
      return;
    }
    setSavingGrand(true);
    try {
      await axios.post(
        `${API}/tax-helper/grand-statements`,
        { name: grandName.trim(), merged_statement_ids: selectedIds },
        { headers: { Authorization: `Bearer ${tok()}` } }
      );
      toast.success("Grand Statement saved");
      setShowSaveGrand(false);
      setSelected({});
      setGrandName("");
      navigate("/grand-statements");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save Grand Statement");
    } finally {
      setSavingGrand(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')} className="gap-1.5" data-testid="back-to-dashboard">
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <div className="flex items-center gap-2">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isYtd ? 'bg-blue-100' : 'bg-purple-100'}`}>
              <HeaderIcon className={`w-5 h-5 ${isYtd ? 'text-blue-600' : 'text-purple-600'}`} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">{headerTitle}</h1>
              <p className="text-xs text-slate-500">{headerSub}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Merge action bar (YTD mode only) */}
        {isYtd && items.length >= 2 && (
          <div className="mb-4 p-3 bg-white border border-slate-200 rounded-lg flex flex-wrap items-center justify-between gap-3" data-testid="grand-merge-bar">
            <p className="text-sm text-slate-600">
              {selectedIds.length === 0
                ? "Tip: tick two or more YTD statements to merge them into a Grand Statement."
                : `${selectedIds.length} Year-to-Date statement${selectedIds.length !== 1 ? 's' : ''} selected`}
            </p>
            <div className="flex items-center gap-2">
              {selectedIds.length > 0 && (
                <Button variant="ghost" size="sm" onClick={() => setSelected({})} data-testid="clear-selection">
                  <X className="w-4 h-4 mr-1" />
                  Clear
                </Button>
              )}
              <Button
                size="sm"
                onClick={openGrandDialog}
                disabled={selectedIds.length < 2}
                className="gap-1.5 bg-purple-600 hover:bg-purple-700"
                data-testid="merge-into-grand-btn"
              >
                <Combine className="w-4 h-4" />
                Merge into Grand Statement
              </Button>
            </div>
          </div>
        )}

        {loading ? (
          <p className="text-center text-slate-500 py-12">Loading…</p>
        ) : items.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <HeaderIcon className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-700 font-medium">
                {isYtd ? "No saved merged statements yet" : "No Grand Statements yet"}
              </p>
              <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
                {isYtd ? (
                  <>
                    Go to <span className="font-medium">Previous Statements</span>, check the boxes on
                    two or more statements, and click <span className="font-medium">"Save as Year-to-Date"</span>.
                  </>
                ) : (
                  <>
                    Open <span className="font-medium">Year-to-Date Statements</span>, tick two or more
                    YTDs, and click <span className="font-medium">"Merge into Grand Statement"</span>.
                  </>
                )}
              </p>
              <Button
                className="mt-4"
                onClick={() => navigate(isYtd ? '/previous-statements' : '/merged-statements')}
                data-testid="go-to-source"
              >
                {isYtd ? "Go to Previous Statements" : "Go to Year-to-Date Statements"}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid={isYtd ? "merged-list" : "grand-list"}>
            {items.map(m => {
              const isSelected = !!selected[m.id];
              return (
                <Card
                  key={m.id}
                  className={`hover:shadow-md transition-shadow ${isSelected ? 'border-purple-400 ring-2 ring-purple-200' : ''}`}
                  data-testid={`${isYtd ? 'merged' : 'grand'}-card-${m.id}`}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-2.5 min-w-0">
                        {isYtd && (
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleSelect(m.id)}
                            className="mt-1"
                            data-testid={`select-merged-${m.id}`}
                            aria-label={`Select ${m.name}`}
                          />
                        )}
                        <div className="min-w-0">
                          <CardTitle className="text-base truncate">{m.name}</CardTitle>
                          <CardDescription className="text-xs flex items-center gap-1 mt-1">
                            <Calendar className="w-3 h-3" />
                            Saved {new Date(m.created_at).toLocaleDateString()}
                          </CardDescription>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <Badge variant="outline">
                          <FileText className="w-3 h-3 mr-1" />
                          {(m.statement_ids || []).length} statements
                        </Badge>
                        {!isYtd && (m.merged_statement_ids || []).length > 0 && (
                          <Badge variant="outline" className="bg-purple-50 border-purple-200 text-purple-700">
                            <Layers className="w-3 h-3 mr-1" />
                            {(m.merged_statement_ids || []).length} YTDs
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-2 flex justify-between gap-2">
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => navigate(`/merged-statements/${m.id}`)}
                      className="flex-1 gap-1.5"
                      data-testid={`view-${m.id}`}
                    >
                      <Eye className="w-4 h-4" />
                      View
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setConfirmDeleteId(m.id)}
                      className="px-2"
                      data-testid={`delete-${m.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-rose-500" />
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      {confirmDeleteId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Delete this {isYtd ? 'merged' : 'Grand'} statement?</CardTitle>
              <CardDescription>
                The source statements{!isYtd ? ' and parent YTDs' : ''} remain untouched.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConfirmDeleteId(null)}>Cancel</Button>
              <Button variant="destructive" onClick={() => remove(confirmDeleteId)} data-testid="confirm-delete">Delete</Button>
            </CardContent>
          </Card>
        </div>
      )}

      {showSaveGrand && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md" data-testid="save-grand-dialog">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Combine className="w-5 h-5 text-purple-600" />
                Save as Grand Statement
              </CardTitle>
              <CardDescription>
                Combine {selectedIds.length} Year-to-Date statements into a single multi-year view.
                You can re-open it any time; edits on underlying statements stay in sync.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-600">Name</label>
                <Input
                  value={grandName}
                  onChange={(e) => setGrandName(e.target.value)}
                  placeholder="e.g. Full Business History 2022–2025"
                  data-testid="grand-name-input"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowSaveGrand(false)} disabled={savingGrand}>
                  Cancel
                </Button>
                <Button onClick={saveGrand} disabled={savingGrand} className="bg-purple-600 hover:bg-purple-700" data-testid="confirm-save-grand">
                  {savingGrand ? "Saving…" : "Save Grand Statement"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

/* ── Detail view ───────────────────────────────────────────────────── */
function MergedDetailView() {
  const { user } = useContext(AuthContext);
  const { mergedId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [expandedCats, setExpandedCats] = useState({});
  const [expandedIncomeCats, setExpandedIncomeCats] = useState({});
  const [txSearch, setTxSearch] = useState("");
  const [chartView, setChartView] = useState("monthly"); // monthly | quarterly | cumulative

  const toggleCat = (cat) =>
    setExpandedCats((prev) => ({ ...prev, [cat]: !prev[cat] }));
  const toggleIncomeCat = (cat) =>
    setExpandedIncomeCats((prev) => ({ ...prev, [cat]: !prev[cat] }));

  useEffect(() => {
    if (!user?.id || !mergedId) return;
    axios.get(`${API}/tax-helper/merged-statements/${mergedId}`, {
      headers: { Authorization: `Bearer ${tok()}` },
    }).then(res => setData(res.data))
      .catch(() => toast.error("Failed to load merged statement"))
      .finally(() => setLoading(false));
  }, [user?.id, mergedId]);

  const exportExcel = async () => {
    if (!data) return;
    setExporting(true);
    try {
      const res = await axios.post(
        `${API}/tax-helper/export-merged-excel`,
        { statement_ids: data.statement_ids },
        { headers: { Authorization: `Bearer ${tok()}` }, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${data.name.replace(/[^a-z0-9]/gi, '_')}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Excel exported");
    } catch (err) {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  if (loading) return <div className="p-12 text-center text-slate-500">Loading…</div>;
  if (!data) return null;
  const s = data.summary;
  const isGrand = data.kind === "grand";
  const backRoute = isGrand ? '/grand-statements' : '/merged-statements';

  // Build categorized rows for breakdown
  const expenseRows = Object.entries(s.expense_categories || {})
    .sort(([, a], [, b]) => b - a);
  const incomeRows = Object.entries(s.income_categories || {})
    .sort(([, a], [, b]) => b - a);

  // Group expense transactions by category, then merge identical descriptions.
  // Use `is_business_expense` to match the backend's expense_categories totals exactly.
  const isExpenseTx = (tx) => !!tx.is_business_expense;
  // Income filter mirrors the backend P&L logic exactly so per-line totals
  // tie out to the Total Income figure.
  const NON_PL_CATS = new Set(['transfers', 'personal_non_business', 'credit_card_payments']);
  const isIncomeTx = (tx) => {
    const t = (tx.transaction_type || '').toLowerCase();
    if (t !== 'credit' && t !== 'deposit') return false;
    return !NON_PL_CATS.has(tx.category);
  };
  const normalizeDesc = (d) =>
    (d || "").toString().trim().replace(/\s+/g, " ").toUpperCase();

  const buildMergedMap = (txFilter, defaultCat) => {
    const map = {};
    (data.combined_transactions || []).forEach((tx) => {
      if (!txFilter(tx)) return;
      const cat = tx.category || defaultCat;
      if (!map[cat]) map[cat] = {};
      const key = normalizeDesc(tx.description);
      if (!map[cat][key]) {
        map[cat][key] = {
          description: tx.description,
          count: 0,
          total: 0,
          dates: [],
          sources: new Set(),
        };
      }
      const g = map[cat][key];
      g.count += 1;
      g.total += Number(tx.amount) || 0;
      g.dates.push(tx.date);
      if (tx.source_filename) g.sources.add(tx.source_filename);
    });
    return map;
  };

  const mergedByCategory = buildMergedMap(isExpenseTx, "other_expenses");
  const mergedIncomeByCategory = buildMergedMap(isIncomeTx, "uncategorized_income");

  const getMergedLineItems = (cat) => {
    const groups = mergedByCategory[cat] || {};
    return Object.values(groups)
      .map((g) => ({ ...g, sources: Array.from(g.sources) }))
      .sort((a, b) => b.total - a.total);
  };
  const getIncomeLineItems = (cat) => {
    const groups = mergedIncomeByCategory[cat] || {};
    return Object.values(groups)
      .map((g) => ({ ...g, sources: Array.from(g.sources) }))
      .sort((a, b) => b.total - a.total);
  };

  // ─── Monthly Income vs Expense aggregation for the dashboard chart ───
  // Bucket every transaction by YYYY-MM. Income = credits/deposits with a
  // P&L-eligible category. Expense = is_business_expense. Net = Income - Expense.
  const parseMonthKey = (dateStr) => {
    if (!dateStr) return null;
    const str = String(dateStr).trim();
    // Try ISO YYYY-MM-DD
    let m = str.match(/^(\d{4})-(\d{1,2})-\d{1,2}/);
    if (m) return `${m[1]}-${m[2].padStart(2, '0')}`;
    // Try MM/DD/YYYY
    m = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (m) return `${m[3]}-${m[1].padStart(2, '0')}`;
    // Last-ditch: Date parser
    const d = new Date(str);
    if (!isNaN(d.getTime())) {
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    }
    return null;
  };

  const monthBuckets = {}; // { 'YYYY-MM': { income, expense } }
  (data.combined_transactions || []).forEach((tx) => {
    const key = parseMonthKey(tx.date);
    if (!key) return;
    if (!monthBuckets[key]) monthBuckets[key] = { income: 0, expense: 0 };
    const amt = Number(tx.amount) || 0;
    if (isIncomeTx(tx)) monthBuckets[key].income += amt;
    if (isExpenseTx(tx)) monthBuckets[key].expense += amt;
  });

  const monthLabel = (key) => {
    const [y, m] = key.split('-');
    const date = new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
    return date.toLocaleString('en-US', { month: 'short', year: '2-digit' });
  };

  const monthlyChartData = Object.keys(monthBuckets)
    .sort()
    .map((key) => ({
      month: monthLabel(key),
      monthKey: key,
      income: Math.round(monthBuckets[key].income * 100) / 100,
      expense: Math.round(monthBuckets[key].expense * 100) / 100,
      net: Math.round((monthBuckets[key].income - monthBuckets[key].expense) * 100) / 100,
    }));

  // Quarterly aggregation
  const quarterBuckets = {}; // 'YYYY-Qn' → {income, expense}
  Object.keys(monthBuckets).forEach((mk) => {
    const [y, m] = mk.split('-');
    const q = Math.floor((parseInt(m, 10) - 1) / 3) + 1;
    const qKey = `${y}-Q${q}`;
    if (!quarterBuckets[qKey]) quarterBuckets[qKey] = { income: 0, expense: 0 };
    quarterBuckets[qKey].income += monthBuckets[mk].income;
    quarterBuckets[qKey].expense += monthBuckets[mk].expense;
  });
  const quarterlyChartData = Object.keys(quarterBuckets)
    .sort()
    .map((key) => {
      const [y, q] = key.split('-');
      return {
        month: `${q} ${y.slice(2)}`,
        monthKey: key,
        income: Math.round(quarterBuckets[key].income * 100) / 100,
        expense: Math.round(quarterBuckets[key].expense * 100) / 100,
        net: Math.round((quarterBuckets[key].income - quarterBuckets[key].expense) * 100) / 100,
      };
    });

  // Cumulative running totals (uses monthly granularity)
  let cumIncome = 0;
  let cumExpense = 0;
  const cumulativeChartData = monthlyChartData.map((row) => {
    cumIncome += row.income;
    cumExpense += row.expense;
    return {
      month: row.month,
      monthKey: row.monthKey,
      income: Math.round(cumIncome * 100) / 100,
      expense: Math.round(cumExpense * 100) / 100,
      net: Math.round((cumIncome - cumExpense) * 100) / 100,
    };
  });

  const activeChartData =
    chartView === 'quarterly' ? quarterlyChartData
    : chartView === 'cumulative' ? cumulativeChartData
    : monthlyChartData;

  const monthsCount = monthlyChartData.length;
  const avgMonthlyIncome = monthsCount ? s.total_income / monthsCount : 0;
  const avgMonthlyExpense = monthsCount ? s.total_business_expenses / monthsCount : 0;
  const profitMargin = s.total_income > 0
    ? (s.net_profit / s.total_income) * 100
    : 0;
  // "Best" is always based on per-period net, computed on the *active* view
  // so the callout label is consistent (e.g. "Best net quarter" when viewing quarterly).
  const bestPeriod = (chartView === 'cumulative' ? monthlyChartData : activeChartData).reduce(
    (best, cur) => (cur.net > (best?.net ?? -Infinity) ? cur : best),
    null
  );
  const bestLabel =
    chartView === 'quarterly' ? 'Best net quarter'
    : 'Best net month'; // cumulative also uses monthly granularity for the callout

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => navigate(backRoute)} className="gap-1.5">
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl font-bold text-slate-900">{data.name}</h1>
                {isGrand && (
                  <Badge variant="outline" className="bg-purple-50 border-purple-200 text-purple-700" data-testid="grand-badge">
                    <Combine className="w-3 h-3 mr-1" />
                    Grand Statement
                  </Badge>
                )}
              </div>
              <p className="text-xs text-slate-500">
                {data.source_statements.length} statements ·{' '}
                {s.period_start} to {s.period_end} · {s.transaction_count} transactions
                {isGrand && (data.merged_statement_ids || []).length > 0 && (
                  <> · merged from {data.merged_statement_ids.length} Year-to-Date views</>
                )}
              </p>
            </div>
          </div>
          <Button onClick={exportExcel} disabled={exporting} className="gap-1.5" data-testid="merged-export-excel">
            <Download className="w-4 h-4" />
            Export Excel
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        {/* Financial dashboard with monthly bar chart */}
        {monthlyChartData.length > 0 && (
          <Card
            className={`overflow-hidden border-0 shadow-md text-white ${isGrand
              ? 'bg-gradient-to-br from-purple-700 via-indigo-700 to-blue-700'
              : 'bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900'
            }`}
            data-testid="financial-dashboard"
          >
            <CardHeader className="pb-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <CardTitle className="text-white text-lg font-semibold tracking-tight">
                    Financial dashboard
                  </CardTitle>
                  <CardDescription className="text-white/70 text-xs">
                    {chartView === 'cumulative'
                      ? `Cumulative income vs business expenses over ${monthsCount} ${monthsCount === 1 ? 'month' : 'months'}`
                      : chartView === 'quarterly'
                      ? `Quarterly income vs business expenses across ${quarterlyChartData.length} ${quarterlyChartData.length === 1 ? 'quarter' : 'quarters'}`
                      : `Monthly income vs business expenses across ${monthsCount} ${monthsCount === 1 ? 'month' : 'months'}`}
                    {s.period_start && s.period_end && ` (${s.period_start} → ${s.period_end})`}.
                  </CardDescription>
                </div>
                {bestPeriod && (
                  <div className="text-right">
                    <p className="text-[11px] uppercase tracking-wider text-white/60">{bestLabel}</p>
                    <p className="text-sm font-semibold text-emerald-300">{bestPeriod.month} · {fmt(bestPeriod.net)}</p>
                  </div>
                )}
              </div>
              {/* View toggle */}
              <div
                className="mt-3 inline-flex rounded-lg bg-white/10 backdrop-blur-sm p-1 ring-1 ring-white/10 text-xs"
                data-testid="chart-view-toggle"
                role="tablist"
                aria-label="Chart view"
              >
                {[
                  { key: 'monthly', label: 'Monthly' },
                  { key: 'quarterly', label: 'Quarterly' },
                  { key: 'cumulative', label: 'Cumulative' },
                ].map((opt) => {
                  const active = chartView === opt.key;
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => setChartView(opt.key)}
                      role="tab"
                      aria-selected={active}
                      data-testid={`chart-view-${opt.key}`}
                      className={`px-3 py-1.5 rounded-md font-medium transition-colors ${
                        active
                          ? 'bg-white text-slate-900 shadow-sm'
                          : 'text-white/75 hover:text-white hover:bg-white/5'
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Mini KPI row */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="dashboard-kpis">
                <div className="rounded-lg bg-white/10 backdrop-blur-sm p-3 ring-1 ring-white/10">
                  <p className="text-[10px] uppercase tracking-wider text-white/60">Avg monthly income</p>
                  <p className="text-base font-semibold text-emerald-300 tabular-nums">{fmt(avgMonthlyIncome)}</p>
                </div>
                <div className="rounded-lg bg-white/10 backdrop-blur-sm p-3 ring-1 ring-white/10">
                  <p className="text-[10px] uppercase tracking-wider text-white/60">Avg monthly expenses</p>
                  <p className="text-base font-semibold text-rose-300 tabular-nums">{fmt(avgMonthlyExpense)}</p>
                </div>
                <div className="rounded-lg bg-white/10 backdrop-blur-sm p-3 ring-1 ring-white/10">
                  <p className="text-[10px] uppercase tracking-wider text-white/60">Net profit margin</p>
                  <p className={`text-base font-semibold tabular-nums ${profitMargin >= 0 ? 'text-blue-300' : 'text-orange-300'}`}>
                    {profitMargin.toFixed(1)}%
                  </p>
                </div>
                <div className="rounded-lg bg-white/10 backdrop-blur-sm p-3 ring-1 ring-white/10">
                  <p className="text-[10px] uppercase tracking-wider text-white/60">Transactions / mo</p>
                  <p className="text-base font-semibold text-white tabular-nums">
                    {monthsCount ? Math.round(s.transaction_count / monthsCount).toLocaleString() : 0}
                  </p>
                </div>
              </div>

              {/* Bar chart */}
              <div
                className="rounded-lg bg-white/5 backdrop-blur-sm p-3 ring-1 ring-white/10"
                data-testid="monthly-chart"
                style={{ width: '100%', height: 320 }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    data={activeChartData}
                    margin={{ top: 12, right: 12, left: 0, bottom: 4 }}
                    barCategoryGap="20%"
                  >
                    <defs>
                      <linearGradient id="incomeBar" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#34d399" stopOpacity={0.95} />
                        <stop offset="100%" stopColor="#10b981" stopOpacity={0.75} />
                      </linearGradient>
                      <linearGradient id="expenseBar" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#fb7185" stopOpacity={0.95} />
                        <stop offset="100%" stopColor="#e11d48" stopOpacity={0.75} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.12)" vertical={false} />
                    <XAxis
                      dataKey="month"
                      tick={{ fill: 'rgba(255,255,255,0.7)', fontSize: 11 }}
                      tickLine={false}
                      axisLine={{ stroke: 'rgba(255,255,255,0.15)' }}
                    />
                    <YAxis
                      tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 11 }}
                      tickLine={false}
                      axisLine={{ stroke: 'rgba(255,255,255,0.15)' }}
                      tickFormatter={(v) => {
                        const n = Number(v) || 0;
                        if (Math.abs(n) >= 1000) return `$${(n / 1000).toFixed(0)}k`;
                        return `$${n}`;
                      }}
                    />
                    <RechartsTooltip
                      cursor={{ fill: 'rgba(255,255,255,0.06)' }}
                      contentStyle={{
                        background: 'rgba(15,23,42,0.95)',
                        border: '1px solid rgba(255,255,255,0.15)',
                        borderRadius: 8,
                        color: '#fff',
                        fontSize: 12,
                      }}
                      formatter={(value, name) => [fmt(value), name]}
                      labelStyle={{ color: 'rgba(255,255,255,0.75)' }}
                    />
                    <Legend
                      wrapperStyle={{ paddingTop: 8, fontSize: 12, color: 'rgba(255,255,255,0.85)' }}
                      iconType="circle"
                    />
                    <Bar
                      dataKey="income"
                      name={chartView === 'cumulative' ? 'Cumulative Income' : 'Income'}
                      fill="url(#incomeBar)"
                      radius={[4, 4, 0, 0]}
                      maxBarSize={36}
                    />
                    <Bar
                      dataKey="expense"
                      name={chartView === 'cumulative' ? 'Cumulative Expenses' : 'Expenses'}
                      fill="url(#expenseBar)"
                      radius={[4, 4, 0, 0]}
                      maxBarSize={36}
                    />
                    <Line
                      type="monotone"
                      dataKey="net"
                      name={chartView === 'cumulative' ? 'Cumulative Net' : 'Net Profit'}
                      stroke="#60a5fa"
                      strokeWidth={2}
                      dot={{ r: 3, fill: '#60a5fa', strokeWidth: 0 }}
                      activeDot={{ r: 5 }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Totals */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Total Income</CardDescription>
              <CardTitle className="text-2xl text-emerald-600 flex items-center gap-2">
                {fmt(s.total_income)}
                <TrendingUp className="w-5 h-5" />
              </CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Business Expenses</CardDescription>
              <CardTitle className="text-2xl text-red-600 flex items-center gap-2">
                {fmt(s.total_business_expenses)}
                <TrendingDown className="w-5 h-5" />
              </CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Net Profit</CardDescription>
              <CardTitle className={`text-2xl flex items-center gap-2 ${s.net_profit >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                {fmt(s.net_profit)}
                <DollarSign className="w-5 h-5" />
              </CardTitle>
            </CardHeader>
          </Card>
        </div>

        {/* Source statements */}
        <Card>
          <CardHeader>
            <CardTitle>Source statements</CardTitle>
            <CardDescription>The individual statements combined into this view. Edits made to any source statement are reflected automatically.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data.source_statements.map(src => (
                <div key={src.id} className="flex items-center justify-between p-3 rounded border border-slate-200 hover:bg-slate-50">
                  <div className="min-w-0">
                    <p className="font-medium text-slate-900 truncate">{src.filename}</p>
                    <p className="text-xs text-slate-500">
                      {src.statement_period_start} → {src.statement_period_end} · {src.account_number} · {src.transaction_count} txns
                    </p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => navigate(`/previous-statements?focus=${src.id}`)} data-testid={`open-source-${src.id}`}>
                    <Eye className="w-4 h-4 mr-1" />
                    Open
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Income breakdown */}
        <Card data-testid="income-breakdown-card">
          <CardHeader>
            <CardTitle>Combined income categories</CardTitle>
            <CardDescription>
              Every credit / deposit that contributed to Total Income, grouped by category. Transactions categorized as Transfers, Credit Card Payments, or Personal Non-Business are intentionally excluded — they don't count as income.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {incomeRows.length === 0 ? (
              <p className="text-center text-slate-400 py-6">No income across these statements yet.</p>
            ) : (
              <div className="space-y-1.5">
                {incomeRows.map(([cat, amt]) => {
                  const items = getIncomeLineItems(cat);
                  const isOpen = !!expandedIncomeCats[cat];
                  const hasItems = items.length > 0;
                  const isUncategorized = cat === 'uncategorized_income';
                  return (
                    <div key={cat} className="rounded border border-transparent hover:border-slate-200 transition-colors">
                      <button
                        type="button"
                        onClick={() => hasItems && toggleIncomeCat(cat)}
                        disabled={!hasItems}
                        className={`w-full flex justify-between items-center px-3 py-2 rounded text-left ${isUncategorized ? 'bg-amber-50 hover:bg-amber-100' : 'bg-emerald-50/60 hover:bg-emerald-50'} ${hasItems ? 'cursor-pointer' : 'cursor-default'}`}
                        data-testid={`income-category-toggle-${cat}`}
                        aria-expanded={isOpen}
                      >
                        <span className="flex items-center gap-2 text-slate-800 capitalize">
                          {hasItems ? (
                            isOpen ? (
                              <ChevronDown className="w-4 h-4 text-slate-500" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-slate-500" />
                            )
                          ) : (
                            <span className="w-4 h-4 inline-block" />
                          )}
                          {isUncategorized ? 'Uncategorized credits' : cat.replace(/_/g, ' ')}
                          {hasItems && (
                            <span className="text-xs text-slate-400 normal-case font-normal">
                              ({items.length} {items.length === 1 ? 'item' : 'items'})
                            </span>
                          )}
                          {isUncategorized && (
                            <Badge variant="outline" className="ml-1 bg-amber-100 border-amber-200 text-amber-800 text-[10px] font-normal">
                              review for transfers
                            </Badge>
                          )}
                        </span>
                        <span className="font-semibold text-emerald-600 tabular-nums">{fmt(amt)}</span>
                      </button>
                      {isOpen && hasItems && (
                        <div
                          className="mt-1 mb-2 ml-6 mr-1 border-l-2 border-slate-200 pl-3"
                          data-testid={`income-category-items-${cat}`}
                        >
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-xs text-slate-500 text-left">
                                <th className="px-2 py-1.5 font-medium">Description</th>
                                <th className="px-2 py-1.5 font-medium text-center w-16">Count</th>
                                <th className="px-2 py-1.5 font-medium">Dates</th>
                                <th className="px-2 py-1.5 font-medium text-right w-28">Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {items.map((item, idx) => (
                                <tr
                                  key={idx}
                                  className="border-t border-slate-100 hover:bg-slate-50/60"
                                  data-testid={`income-line-item-${cat}-${idx}`}
                                >
                                  <td className="px-2 py-1.5 text-slate-800 align-top">
                                    {item.description}
                                  </td>
                                  <td className="px-2 py-1.5 text-center text-slate-600 tabular-nums align-top">
                                    {item.count}
                                  </td>
                                  <td className="px-2 py-1.5 text-xs text-slate-500 align-top">
                                    {item.dates.length <= 3
                                      ? item.dates.join(', ')
                                      : `${item.dates.slice(0, 3).join(', ')} +${item.dates.length - 3} more`}
                                  </td>
                                  <td className="px-2 py-1.5 text-right tabular-nums font-medium text-emerald-600 align-top">
                                    {fmt(item.total)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
                <div className="flex justify-between items-center px-3 py-2.5 rounded border-t-2 border-emerald-200 bg-emerald-50 mt-3">
                  <span className="font-semibold text-slate-800">Total Income</span>
                  <span className="font-bold text-emerald-700 tabular-nums text-lg">{fmt(s.total_income)}</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Expense breakdown */}
        <Card>
          <CardHeader>
            <CardTitle>Combined expense categories</CardTitle>
            <CardDescription>Sum of business expenses across every source statement. Click a category to see its merged line items.</CardDescription>
          </CardHeader>
          <CardContent>
            {expenseRows.length === 0 ? (
              <p className="text-center text-slate-400 py-6">No business expenses across these statements yet.</p>
            ) : (
              <div className="space-y-1.5">
                {expenseRows.map(([cat, amt]) => {
                  const items = getMergedLineItems(cat);
                  const isOpen = !!expandedCats[cat];
                  const hasItems = items.length > 0;
                  return (
                    <div key={cat} className="rounded border border-transparent hover:border-slate-200 transition-colors">
                      <button
                        type="button"
                        onClick={() => hasItems && toggleCat(cat)}
                        disabled={!hasItems}
                        className={`w-full flex justify-between items-center px-3 py-2 rounded bg-slate-50 hover:bg-slate-100 text-left ${hasItems ? 'cursor-pointer' : 'cursor-default'}`}
                        data-testid={`merged-category-toggle-${cat}`}
                        aria-expanded={isOpen}
                      >
                        <span className="flex items-center gap-2 text-slate-800 capitalize">
                          {hasItems ? (
                            isOpen ? (
                              <ChevronDown className="w-4 h-4 text-slate-500" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-slate-500" />
                            )
                          ) : (
                            <span className="w-4 h-4 inline-block" />
                          )}
                          {cat.replace(/_/g, ' ')}
                          {hasItems && (
                            <span className="text-xs text-slate-400 normal-case font-normal">
                              ({items.length} {items.length === 1 ? 'item' : 'items'})
                            </span>
                          )}
                        </span>
                        <span className="font-semibold text-red-600 tabular-nums">{fmt(amt)}</span>
                      </button>
                      {isOpen && hasItems && (
                        <div
                          className="mt-1 mb-2 ml-6 mr-1 border-l-2 border-slate-200 pl-3"
                          data-testid={`merged-category-items-${cat}`}
                        >
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-xs text-slate-500 text-left">
                                <th className="px-2 py-1.5 font-medium">Description</th>
                                <th className="px-2 py-1.5 font-medium text-center w-16">Count</th>
                                <th className="px-2 py-1.5 font-medium">Dates</th>
                                <th className="px-2 py-1.5 font-medium text-right w-28">Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {items.map((item, idx) => (
                                <tr
                                  key={idx}
                                  className="border-t border-slate-100 hover:bg-slate-50/60"
                                  data-testid={`merged-line-item-${cat}-${idx}`}
                                >
                                  <td className="px-2 py-1.5 text-slate-800 align-top">
                                    {item.description}
                                  </td>
                                  <td className="px-2 py-1.5 text-center text-slate-600 tabular-nums align-top">
                                    {item.count}
                                  </td>
                                  <td className="px-2 py-1.5 text-xs text-slate-500 align-top">
                                    {item.dates.length <= 3
                                      ? item.dates.join(', ')
                                      : `${item.dates.slice(0, 3).join(', ')} +${item.dates.length - 3} more`}
                                  </td>
                                  <td className="px-2 py-1.5 text-right tabular-nums font-medium text-red-600 align-top">
                                    {fmt(item.total)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
                <div className="flex justify-between items-center px-3 py-2.5 rounded border-t-2 border-red-200 bg-red-50 mt-3">
                  <span className="font-semibold text-slate-800">Total Business Expenses</span>
                  <span className="font-bold text-red-700 tabular-nums text-lg">{fmt(s.total_business_expenses)}</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* All transactions */}
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <CardTitle>All transactions ({s.transaction_count})</CardTitle>
                <CardDescription>Every transaction across the merged statements. Use the source filename column to trace back to a specific statement.</CardDescription>
              </div>
              <div className="relative w-full sm:w-72 shrink-0">
                <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                <Input
                  value={txSearch}
                  onChange={(e) => setTxSearch(e.target.value)}
                  placeholder="Search transactions…"
                  className="pl-8 pr-8 h-9"
                  data-testid="tx-search-input"
                />
                {txSearch && (
                  <button
                    type="button"
                    onClick={() => setTxSearch("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    aria-label="Clear search"
                    data-testid="tx-search-clear"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {(() => {
              const q = txSearch.trim().toLowerCase();
              const filteredTxns = q
                ? (data.combined_transactions || []).filter((tx) => {
                    const amt = Number(tx.amount) || 0;
                    const fields = [
                      tx.description,
                      tx.source_filename,
                      tx.category,
                      tx.date,
                      String(tx.amount ?? ''),
                      amt.toFixed(2),
                    ];
                    return fields.some((v) =>
                      (v || '').toString().toLowerCase().includes(q)
                    );
                  })
                : (data.combined_transactions || []);
              return (
                <>
                  {q && (
                    <p className="text-xs text-slate-500 mb-2" data-testid="tx-search-result-count">
                      {filteredTxns.length === 0
                        ? `No transactions match "${txSearch}"`
                        : `Showing ${filteredTxns.length} of ${s.transaction_count} transactions`}
                    </p>
                  )}
                  <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 sticky top-0 text-slate-600 text-left">
                        <tr>
                          <th className="px-3 py-2 font-medium">Date</th>
                          <th className="px-3 py-2 font-medium">Description</th>
                          <th className="px-3 py-2 font-medium">Source</th>
                          <th className="px-3 py-2 font-medium">Category</th>
                          <th className="px-3 py-2 font-medium text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody data-testid="tx-table-body">
                        {filteredTxns.map((tx, i) => (
                          <tr key={i} className="border-t border-slate-100 hover:bg-slate-50/60">
                            <td className="px-3 py-2 whitespace-nowrap">{tx.date}</td>
                            <td className="px-3 py-2">{tx.description}</td>
                            <td className="px-3 py-2 text-xs text-slate-500 truncate max-w-[160px]" title={tx.source_filename}>{tx.source_filename}</td>
                            <td className="px-3 py-2 text-xs">
                              {tx.category ? <span className="capitalize">{tx.category.replace(/_/g, ' ')}</span> : <span className="text-slate-400">—</span>}
                            </td>
                            <td className={`px-3 py-2 text-right tabular-nums font-medium ${tx.transaction_type === 'credit' || tx.transaction_type === 'deposit' ? 'text-emerald-600' : 'text-red-600'}`}>
                              {tx.transaction_type === 'credit' || tx.transaction_type === 'deposit' ? '+' : '-'}{fmt(tx.amount)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              );
            })()}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

/* ── Router export ─────────────────────────────────────────────────── */
export default function MergedStatements({ kind = "ytd" }) {
  const { mergedId } = useParams();
  return mergedId ? <MergedDetailView /> : <MergedListView kind={kind} />;
}
